import { getHSLAString } from "@aichat/aichat-lib";
import { MessageComponentResponse } from "@aichat/types/socket/widget.types";
import { WidgetConfig } from "@aichat/types/widget";
import { ExpandableChatFooter } from "@aichat/ui/components/ui/chat/expandable-chat";
import { Typography } from "@aichat/ui/components/ui/typography";
import useLanguage from "@aichat/ui/hooks/use-language";
import useVoice from "@aichat/ui/hooks/use-voice";
import { cn } from "@aichat/ui/lib/utils";
import { Loader, MicIcon, SquareIcon, XIcon } from "lucide-react";
import { useEffect, useState } from "react";
import OvalIcon from "../../icons/OvalIcon";
import AudioVisualizer from "./audio-visualizer";
import SelectLanguage from "./select-language";
import { useWebWidgetConfig } from "./use-web-widget-config";

interface IVoiceUIProps {
  config: WidgetConfig;
  onClose: () => void;
  isStreaming: boolean;
  // eslint-disable-next-line no-unused-vars
  onChange: (text: string) => void;
  onTranscript: (transcript: string) => void;
  onFinal: () => void;
  onInterrupt: () => void;
  deepgramApiKey: string;
  isVoiceBotSpeaking: boolean;
  isMessageLoading: boolean;
  isRecording: boolean;
  // eslint-disable-next-line no-unused-vars
  setIsRecording: (isRecording: boolean) => void;
  voiceOverlay: MessageComponentResponse | null;
  hideVoiceOverlay: () => void;
  clientDomain: string;
}

export default function VoiceUI(props: IVoiceUIProps) {
  const {
    config,
    deepgramApiKey,
    onFinal,
    onChange,
    onInterrupt,
    isVoiceBotSpeaking,
    isRecording,
    setIsRecording,
    onClose,
    onTranscript,
    clientDomain,
  } = props;
  const [language, setLanguage] = useLanguage();
  const { isThemeSleek } = useWebWidgetConfig();
  const { isSpeechFinal, frequencyData, isConnected } = useVoice({
    deepgramApiKey,
    isRecording,
    setIsRecording,
    onChange,
    onTranscript,
    language,
    isVoiceBotSpeaking,
    clientDomain,
  });

  const [headerHeight, setHeaderHeight] = useState(0);

  useEffect(() => {
    const header = document.getElementById("expandable-chat-header");
    if (header) {
      setHeaderHeight(header.clientHeight);
    }
  }, []);

  useEffect(() => {
    if (isRecording) {
      isSpeechFinal.current = false;
      return;
    }
    if (isSpeechFinal.current) {
      isSpeechFinal.current = false;
      onFinal();
    }
  }, [isRecording]);

  const brandHSLAString = getHSLAString(config.brandColor);

  return (
    <>
      <ExpandableChatFooter
        className={cn("flex relative px-4 pt-1.5 pb-8 border-0 justify-center")}
      >
        <SelectLanguage
          language={language}
          setLanguage={setLanguage}
          supportedLanguages={config.supportedLanguages}
          config={config}
        />
        <div className="absolute right-0 left-0 bottom-0 -z-10">
          <OvalIcon fill={brandHSLAString} />
        </div>
        <div
          className={cn(
            "flex items-center justify-between py-2.5",
            isThemeSleek
              ? "w-full h-16 px-3 bg-gradient-to-b from-neutral-100 from-0% to-neutral-200 to-100% rounded-3xl shadow-[0px_1px_1px_0px_rgba(0,0,0,0.02)] shadow-[0px_4px_12px_0px_rgba(0,0,0,0.09)] backdrop-blur-[44px]"
              : "px-6 rounded-[44px] justify-center gap-6",
          )}
        >
          <div className="flex flex-col gap-1 items-center w-[50px]">
            <button
              className={cn(
                "rounded-full p-2",
                isThemeSleek
                  ? "bg-gradient-to-b from-white from-0% to-white/80 to-30%"
                  : "bg-gradient-to-b from-background-50 to-background-100 border border-secondary shadow-sm",
                isVoiceBotSpeaking ? undefined : "cursor-not-allowed",
              )}
              onClick={isVoiceBotSpeaking ? onInterrupt : undefined}
            >
              {isVoiceBotSpeaking ? (
                <SquareIcon
                  size={24}
                  strokeWidth={1.5}
                  color={brandHSLAString}
                />
              ) : (
                <MicIcon size={24} strokeWidth={1.5} color={brandHSLAString} />
              )}
            </button>
            {!isThemeSleek && (
              <Typography variant="tiny" className="text-neutral-500">
                {isVoiceBotSpeaking ? "Interrupt" : "Listening"}
              </Typography>
            )}
          </div>
          <AudioVisualizer
            brandHSLAString={brandHSLAString}
            frequencyData={frequencyData}
          />
          <div className="flex flex-col gap-1 items-center">
            <button
              onClick={() => {
                onClose();
                onInterrupt();
              }}
              className={cn(
                "rounded-full p-2 bg-gradient-to-b from-red-500 to-red-600 border border-red-600 shadow-sm",
              )}
            >
              <XIcon strokeWidth={1.5} size={24} className="text-white" />
            </button>
            {!isThemeSleek && (
              <Typography variant="tiny" className="text-neutral-500">
                End
              </Typography>
            )}
          </div>
        </div>
      </ExpandableChatFooter>
      {!isConnected && (
        <div
          style={{
            height: `calc(100% - ${headerHeight}px)`,
          }}
          className="absolute bottom-0 w-full bg-white/70 z-40"
        >
          <div className="flex flex-col items-center justify-center h-full gap-2">
            <Loader size={24} className="animate-spin" />
            <Typography variant="tiny" className="text-neutral-500">
              Connecting...
            </Typography>
          </div>
        </div>
      )}
    </>
  );
}
